import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Component, Inject, signal, WritableSignal } from '@angular/core';
import { ButtonPadding, ButtonVariant, PlButtonType } from '@app/components/button/button.component';
import { IconFrame } from '@app/icon-library/lib/pl-icon.component';
import { PlIcon } from '@app/icon-library/icons';
import { FormBuilder, FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import { LifemapService, StartEndDateTime } from '@shared/services/lifemap/lifemap.service';
import { differenceInMinutes } from 'date-fns';
import { TimeFormat } from '@app/components/pl-timepicker/pl-timepicker.component';

enum CustomErrors {
  durationTooLong = 'durationTooLong',
}

enum FormControlName {
  sleepAt = 'sleepAt',
  wakeUpAt = 'wakeUpAt',
}

interface SleepHoursForm {
  [FormControlName.sleepAt]: FormControl<string>;
  [FormControlName.wakeUpAt]: FormControl<string>;
}

export interface SleepHours {
  sleepAt: string;
  wakeUpAt: string;
}

export interface ModalSleepHoursInputs {
  timeFormat: TimeFormat;
}

@Component({
  selector: 'pl-modal-sleep-hours',
  templateUrl: './modalSleepHours.component.html',
})
export class ModalSleepHoursComponent {
  public timeFormat!: WritableSignal<TimeFormat>;

  public form!: FormGroup<SleepHoursForm>;

  public readonly ButtonPadding: typeof ButtonPadding = ButtonPadding;
  public readonly ButtonVariant: typeof ButtonVariant = ButtonVariant;
  public readonly CustomErrors: typeof CustomErrors = CustomErrors;
  public readonly FormControlName: typeof FormControlName = FormControlName;
  public readonly IconFrame: typeof IconFrame = IconFrame;
  public readonly PlButtonType: typeof PlButtonType = PlButtonType;
  public readonly PlIcon: typeof PlIcon = PlIcon;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ModalSleepHoursInputs,
    private dialogRef: MatDialogRef<ModalSleepHoursComponent, SleepHours | null>,
    private lifemapService: LifemapService,
  ) {
    this.timeFormat = signal(data.timeFormat);
    this.createForm();
  }

  public closeModal(): void {
    this.dialogRef.close(null);
  }

  public saveSleepHours(): void {
    if (this.form.valid) {
      this.dialogRef.close({
        sleepAt: this.form.controls[FormControlName.sleepAt].value,
        wakeUpAt: this.form.controls[FormControlName.wakeUpAt].value,
      });
    } else {
      this.form.controls[FormControlName.sleepAt].markAsDirty();
      this.form.controls[FormControlName.wakeUpAt].markAsDirty();
    }
  }

  private createForm(): void {
    const fb: FormBuilder = new FormBuilder();
    this.form = fb.group<SleepHoursForm>({
      [FormControlName.sleepAt]: fb.nonNullable.control<string>('23:00', []),
      [FormControlName.wakeUpAt]: fb.nonNullable.control<string>('07:00', []),
    });

    this.form.setValidators([this.validateTimes.bind(this)]);
    // TONIO TODO Add custom validator so that they can't enter the same value for both
  }

  private validateTimes(): ValidationErrors | null {
    const sleepAt: string = this.form.controls[FormControlName.sleepAt].value;
    const wakeUpAt: string = this.form.controls[FormControlName.wakeUpAt].value;

    if (sleepAt !== '' && wakeUpAt !== '') {
      const numberHoursDiff: number = this.getHoursDifferencesBetweenTimes(sleepAt, wakeUpAt);
      if (numberHoursDiff > 12) {
        const error: ValidationErrors = {
          [CustomErrors.durationTooLong]: numberHoursDiff,
        };

        this.form.controls[FormControlName.sleepAt].setErrors(error);
        this.form.controls[FormControlName.wakeUpAt].setErrors(error);

        return error;
      } else {
        this.form.controls[FormControlName.sleepAt].setErrors(null);
        this.form.controls[FormControlName.wakeUpAt].setErrors(null);
        return null;
      }
    } else {
      const requiredError: ValidationErrors = {
        required: true,
      };
      this.form.controls[FormControlName.sleepAt].setErrors(!sleepAt ? requiredError : null);
      this.form.controls[FormControlName.wakeUpAt].setErrors(!wakeUpAt ? requiredError : null);
      return requiredError;
    }
  }

  private getHoursDifferencesBetweenTimes(sleepAt: string, wakeUpAt: string): number {
    if (sleepAt === wakeUpAt) {
      return 24;
    }

    const arbitraryDate: Date = this.lifemapService.getCalendarStartDate();
    const { startDateTime, endDateTime }: StartEndDateTime = this.lifemapService.computeNewStartEndDatesBasedOnTime(
      arbitraryDate,
      sleepAt,
      wakeUpAt,
    );

    const diffInMinutes: number = differenceInMinutes(endDateTime, startDateTime);
    return +(diffInMinutes / 60).toFixed(1);
  }
}
